import React, { useState, useEffect, useRef, useMemo } from "react";
import DatePicker from "react-datepicker";
import moment from "moment-timezone";
import FrozenMoment from "frozen-moment";
import { Col } from "react-bootstrap";
import GYAxios from "../../../util/GYAxios";
import { useSolarCalc } from "../../../util/calculations";
import MaterialTable from "material-table";
import summarytable from "./summarystyle.module.css";
import { isITUser } from "../../../util/roles";
import { useUser } from "../../../util/user";
import { isNaN, float_format } from "../../../util/formatter";
import { useUserProps } from "../../../util/userDBContext";
import Papa from "papaparse";

// helper to make slug_field_names
const slugify = str => str.toLowerCase()
    .replace(/[\s/()–—,…]+/g, '_')
    .replace(/__+/g, '_')
    .replace(/^_|_$/g, '');

const MonthlyLossesRPRReport = () => {
    const width = 150;

    const [subs, setSubs] = useState([]);
    const [mains, setMains] = useState([]);

    const [csvData, setCsvData] = useState(null);

    const user = useUser();
    const roles = user.roles;

    const [month_data, setMonthData] = useState([]);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // check if today is the second day of the month or after the second day of the month
    const isSecondDay = moment().date() >= 2;
    const dateState = isSecondDay ? new Date(moment().subtract(1, "months").toDate()) : new Date(moment().subtract(2, "months").toDate());
    const [startDate, setStartDate] = useState(dateState);
    const endDate = isITUser(roles) ? new Date() : dateState;
    const defaultMonthYear = useRef(startDate);
    const { loggedCountry } = useUserProps();
    const [selectedCountry, setSelectedCountry] = useState(loggedCountry);
    const defaultCountry = useRef(selectedCountry);

    const solarCalc = useSolarCalc();

    useEffect(() => {
        // fetch sub‑categories (ic_shown=1) and main unavailability (ic_shown=1) list
        Promise.all([
            GYAxios.get('/reports/un_cat_sub?ic_shown=1'),
            GYAxios.get('/reports/unavailability?ic_shown=1')
        ]).then(([sRes, mRes]) => {
            setSubs(sRes.data.sort((a, b) => a.name.localeCompare(b.name)));
            setMains(mRes.data.sort((a, b) => a.name.localeCompare(b.name)));
        }).catch(console.error);
    }, []);

    const icreport_cols = useMemo(() => {
        const cols = [
            { title: "Company", field: "company", width },
            { title: "Site Name", field: "site_name", width },
            { title: "Country", field: "country", cellStyle: { display: "none" }, headerStyle: { display: "none" }, width },
            { title: "SF Project ID", field: "SF_project_id", width },
            { title: "COD", field: "cod", width },
            { title: "Capacity (kWh)", field: "power", width },
            { title: "Contract Scheme", field: "contract_id", width },
            { title: "Actual Production (kWh)", field: "actual_prod", width },
            { title: "Actual Irradiation (kWh/m²)", field: "actual_irr", width },
            { title: "Module Temp (°C)", field: "module_temp", width },
        ];

        const tail = [
            { title: "ZE Irradiation (kWh/m\xB2)", field: "m_ZE_irr", width },
            { title: "ZE Production (kWh)", field: "m_ZE_prod", width },
            { title: "ZE loss (kWh)", field: "m_ZE_loss", width },
            { title: "RPR (%)", field: "RPR", width },
            { title: "DEO (kWh)", field: "DEO", width },
            { title: "Unused (kWh)", field: "unused", width },
            { title: "Curtailment (kWh)", field: "curtailment", width }
        ];

        const byMain = subs.reduce((acc, sub) => {
            acc[sub.un_id] = acc[sub.un_id] || [];
            acc[sub.un_id].push(sub);
            return acc;
        }, {});

        mains.forEach(main => {
            const groupSubs = byMain[main.id] || [];
            groupSubs.forEach(sub => {
                cols.push({
                    title: `${sub.name} Irradiation (kWh/m²)`,
                    field: `sub_${sub.id}_irr`,
                    width
                });
                cols.push({
                    title: `${sub.name} Production (kWh)`,
                    field: `sub_${sub.id}_prod`,
                    width
                });
                cols.push({
                    title: `${sub.name} Loss (kWh)`,
                    field: `sub_${sub.id}_loss`,
                    width
                });
            });

            cols.push({
                title: `Not defined ${main.name} losses (kWh)`,
                field: `not_defined_${slugify(main.name)}`,
                width
            });

            cols.push({
                title: `${main.name} Irradiation (kWh/m²)`,
                field: `m_${main.name}_irr`, //
                width
            });
            cols.push({
                title: `${main.name} Production (kWh)`,
                field: `m_${main.name}_prod`,
                width
            });
            cols.push({
                title: `${main.name} Loss (kWh)`,
                field: `m_${main.name}_loss`,
                width
            });
        });

        cols.push(...tail);
        return cols;
    }, [subs, mains, width]);

    async function fetch_m_calc(uri) {
        setMonthData([]);
        try {
            const response = await GYAxios.get(uri);
            if (response.status === 200) {
                const data = response.data;
                if (data && data.length > 0) {
                    setMonthData(data);
                }
            }
        } catch (error) {
            console.log("error", error);
            return [];
        }
    }

    function fetch_csv(startDateStr) {
        setCsvData(null);
        try {
            Papa.parse(`/sub_ic/grouped_agg_by_site_and_subcat_${selectedCountry}_${startDateStr}.csv`, {
                download: true,
                header: true,
                dynamicTyping: true,
                complete: ({ data }) => setCsvData(data)
            });
        } catch (error) {
            console.error("Error fetching CSV data:", error);
            setCsvData(null); // Reset csvData in case of error
        }
    }

    function process_data() {
        if (month_data.length === 0 || !csvData) return [];

        const groupedCSV = csvData.reduce((acc, row) => {
            const sfID = row["Salesforce ID"];
            if (!acc[sfID]) acc[sfID] = [];
            acc[sfID].push(row);
            return acc;
        }, {});

        const byMain = subs.reduce((acc, sub) => {
            acc[sub.un_id] = acc[sub.un_id] || [];
            acc[sub.un_id].push(sub);
            return acc;
        }, {});

        const processed_month_data = month_data.map((row) => {
            const sfID = row.SF_project_id;
            const csvGroup = groupedCSV[sfID] || [];

            const subData = {};            

            const RPR = row.RPR ? parseFloat(row.RPR / 100) : 0;

            const TA_loss = solarCalc.cal_inc_loss(RPR, row.power, row.m_TA_irr, row.m_TA_prod);
            const GA_loss = solarCalc.cal_inc_loss(RPR, row.power, row.m_GA_irr, row.m_GA_prod);
            const TPA_loss = solarCalc.cal_inc_loss(RPR, row.power, row.m_TPA_irr, row.m_TPA_prod);
            const CA_loss = solarCalc.cal_inc_loss(RPR, row.power, row.m_CA_irr, row.m_CA_prod);
            const _ZE_loss = solarCalc.cal_inc_loss(RPR, row.power, row.m_ZE_irr, row.m_ZE_prod);
            const ZE_loss = _ZE_loss < 0 ? 0 : _ZE_loss;
            
            let DEO = solarCalc.cal_new_DEO(RPR, row.power, row.actual_irr, TPA_loss, GA_loss);
            // DEO should not be less than actual production
            DEO = DEO < row.actual_prod ? row.actual_prod : DEO;

            const unused = row.contract_id === "SC" ? solarCalc.cal_unused_scheme(DEO, row.actual_prod) : 0;
            const curtailment = row.contract_id === "FIT" ? solarCalc.cal_unused_scheme(DEO, row.actual_prod) : 0;

            row.RPR = float_format(RPR * 100, 2);
            row.m_TA_loss = float_format(TA_loss, 2);
            row.m_GA_loss = float_format(GA_loss, 2);
            row.m_TPA_loss = float_format(TPA_loss, 2);
            row.m_CA_loss = float_format(CA_loss, 2);
            row.m_ZE_loss = float_format(ZE_loss, 2);
            row.DEO = float_format(DEO, 2);
            row.unused = float_format(unused, 2);
            row.actual_prod = float_format(row.actual_prod, 2);
            row.actual_irr = float_format(parseFloat(row.actual_irr) / 6000, 2);
            row.curtailment = float_format(curtailment, 2);
            row.m_CA_irr = float_format(parseFloat(row.m_CA_irr) / 6000, 2);
            row.m_GA_irr = float_format(parseFloat(row.m_GA_irr) / 6000, 2);
            row.m_TA_irr = float_format(parseFloat(row.m_TA_irr) / 6000, 2);
            row.m_TPA_irr = float_format(parseFloat(row.m_TPA_irr) / 6000, 2);
            row.m_ZE_irr = float_format(parseFloat(row.m_ZE_irr) / 6000, 2);
            row.m_CA_prod = float_format(row.m_CA_prod, 2);
            row.m_GA_prod = float_format(row.m_GA_prod, 2);
            row.m_TA_prod = float_format(row.m_TA_prod, 2);
            row.m_TPA_prod = float_format(row.m_TPA_prod, 2);
            row.m_ZE_prod = float_format(row.m_ZE_prod, 2);
            row.cod = moment(row.COD).format("YYYY-MM-DD");
            row.module_temp = float_format(row.module_temp, 2);

            // Build a lookup of sub_id to CSV row
            const subIdMap = {};
            csvGroup.forEach(csvRow => {
                subIdMap[csvRow["un_sub_cat_id"]] = csvRow;
            });

            // Iterate through ALL subs (from master list)
            subs.forEach(sub => {
                const subId = sub.id;
                const match = subIdMap[subId];
                const prod = match ? parseFloat(match["prod"] || 0) : 0;
                const irr = match ? parseFloat(match["irr"] || 0) : 0;

                const _formatted_irr = irr ? parseFloat(parseFloat(irr).toFixed(2)) : 0;
                const _formatted_prod = prod ? parseFloat(parseFloat(prod).toFixed(2)) : 0;
                const loss = match ? solarCalc.cal_inc_loss(RPR, row.power, _formatted_irr*6000, _formatted_prod) : 0;

                subData[`sub_${subId}_prod`] = float_format(prod, 2);
                subData[`sub_${subId}_irr`] = float_format(irr, 2);
                subData[`sub_${subId}_loss`] = float_format(loss, 2);
            });

            // calculate sum of sub losses of each main
            mains.forEach(main => {
                const groupSubs = byMain[main.id] || [];
                let totalLoss = 0;
                groupSubs.forEach(sub => {
                    
                    const subId = sub.id;
                    
                    // remove "," from formatted string
                    const _loss = subData[`sub_${subId}_loss`] ? subData[`sub_${subId}_loss`].replace(/,/g, '') : 0;
                    const loss = parseFloat(_loss);                    
                    totalLoss += parseFloat(loss);
                });

                const _main_loss = row[`m_${main.name}_loss`] ? row[`m_${main.name}_loss`].replace(/,/g, '') : 0;
                const main_loss = parseFloat(_main_loss);
                const not_defined_loss = main_loss - totalLoss;
                subData[`not_defined_${slugify(main.name)}`] = not_defined_loss < 0 ? 0 : float_format(not_defined_loss, 2);
            });

            return {...row, ...subData};
        })
       
        return processed_month_data;
    }

    async function reload() {
        setData([]);
        setLoading(true);

        let _startDate = FrozenMoment(startDate);
        _startDate = moment(_startDate).format("YYYY-MM");
        const startDateStr = moment(_startDate).format("MMMYY");

        const monthly_uri = `/reports/monthly_ICRPR?monthyear=${_startDate}&selectedCountry=${selectedCountry}`;

        await fetch_m_calc(monthly_uri);
        fetch_csv(startDateStr);
    }

    useEffect(() => {
        setLoading(true);
        const monthyear = moment(startDate).format("YYYY-MM");
        const monthly_uri = `/reports/monthly_ICRPR?monthyear=${monthyear}&selectedCountry=${selectedCountry}`;
        fetch_m_calc(monthly_uri);
        const startDateStr = moment(startDate).format("MMMYY");
        fetch_csv(startDateStr);
    }, []); // [] to run once on mount

    useEffect(() => {
        const timer = setTimeout(() => {
            if (defaultMonthYear.current && startDate !== defaultMonthYear.current) {
                defaultMonthYear.current = startDate;
                reload();
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [startDate, defaultMonthYear]); 

    useEffect(() => {
        const timer = setTimeout(() => {
            if (defaultCountry.current && selectedCountry !== defaultCountry.current) {
                defaultCountry.current = selectedCountry;
                reload();
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [selectedCountry]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (month_data.length > 0 && csvData) {
                const processed = process_data(); 
                setData(processed);
                setLoading(false);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [month_data, csvData]); 

    useEffect(() => {
        if (loading) {
            const timer = setTimeout(() => {
                setLoading(false);
            }, 10000); // 10s = 10000ms
            return () => clearTimeout(timer);
        }
    }, [loading]);


    return (<>
        <div className="panel-body">
            <h3>INTERNATIONAL COORDINATION REPORT</h3>
            <Col md="4">
                <div className="month-selector">
                    Choose another Month: &nbsp;&nbsp;
                    <div style={{ display: "inline-block", width: '150px' }}>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            dateFormat="MMM yyyy"
                            disabled={loading}
                            showMonthYearPicker
                            showTwoColumnMonthYearPicker
                            maxDate={endDate}
                        />
                    </div>
                </div>
            </Col>
            <Col md="8" style={{ textAlign: "right" }}>
                <div className="month-selector">
                    <button
                        disabled={selectedCountry == "VN"}
                        className={"btn btn-primary"}
                        onClick={() => {
                            if (selectedCountry !== "VN") setSelectedCountry("VN");
                        }}
                    >VN</button>
                    <button
                        disabled={selectedCountry == "TH"}
                        className={"btn btn-primary"}
                        onClick={() => {
                            if (selectedCountry !== "TH") setSelectedCountry("TH");
                        }}
                    >TH</button>
                    <button
                        disabled={selectedCountry == "PH"}
                        className={"btn btn-primary"}
                        onClick={() => {
                            if (selectedCountry !== "PH") setSelectedCountry("PH");
                        }}
                    >PH</button>
                </div>
            </Col>
        </div>
        <div className="panel-body">
            <MaterialTable
                title={
                    <div>
                        <h5>
                            Monthly Report {" "}
                            <span
                                className="	glyphicon glyphicon-refresh"
                                onClick={() => reload()}
                            ></span>
                        </h5>
                    </div>
                }
                columns={icreport_cols}
                data={data}
                isLoading={loading}
                style={summarytable}
                options={{
                    /* showTitle: false, */
                    exportButton: true,
                    exportAllData: true,
                    grouping: true,
                    pageSize: 10,
                    pageSizeOptions: [10, 20, 30, 50, 100],
                    headerStyle: {
                        fontSize: "1rem",
                        textAlign: "center",
                    },
                    cellStyle: { textAlign: "center", },
                    fixedColumns: { left: 2, },
                    emptyRowsWhenPaging: false,
                    filtering: false,
                    exportFileName: `IC Report - ${moment(startDate).format("MMMM YYYY")}`,
                }}
            />
        </div></>
    );
};

export default React.memo(MonthlyLossesRPRReport);
